import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Check } from "lucide-react";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { toast } from "react-toastify";
import Modal from "components/Modal";
import { Button } from "components/standardElements";
import { COLORS, FONTSIZE, FONTWEIGHT } from "helpers/designConstants";
import { SIZE_ORDER } from "services/supabaseSearch";
import { humanizeDate } from "./humanizeDate";
import {
  svgUrlToPngDataUrl,
  fetchAndDownscaleToJpeg,
  fetchAndDownscaleToPng,
} from "./svgToPng";
import { computeIssueDrift } from "./contactDrift";

function Checkbox({ checked, onChange, ariaLabel }) {
  return (
    <CheckboxWrap>
      <HiddenInput
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-label={ariaLabel}
      />
      <CheckboxBox $checked={checked}>
        {checked && <Check size={12} strokeWidth={3} />}
      </CheckboxBox>
    </CheckboxWrap>
  );
}

const STORMTECH_FOOTER_LOGO = "/stormtech-logo.png";

// Only rows the rep hasn't yet acted on belong in an outgoing PDF. Contacted
// means the rep already messaged the distributor about this row — including
// it in a fresh PDF would double-ask. Dismissed/resolved are terminal.
// Contacted rows appear ONLY when the scanner has detected drift since the
// rep last called — those are the rows the rep needs to follow up on
// (shipment date moved, severity changed, sizes recovered), so the PDF is
// the natural artifact to bring to that call.
const ACTIVE_STATUSES = new Set(["new", "acknowledged", "contacted"]);

// Balanced column split for the size pill grid. See the matching helper in
// StockIssuesPDFDocument.jsx for rationale — same math drives PDF and modal.
const TARGET_ROWS_PER_COLUMN = 3;
const MAX_COLUMNS = 3;

function splitIntoColumns(sizes) {
  const n = sizes.length;
  if (n === 0) return [];
  const cols = Math.min(
    MAX_COLUMNS,
    Math.max(1, Math.ceil(n / TARGET_ROWS_PER_COLUMN))
  );
  const rows = Math.ceil(n / cols);
  const result = [];
  for (let c = 0; c < cols; c += 1) {
    result.push(sizes.slice(c * rows, (c + 1) * rows));
  }
  return result;
}

/**
 * Filters a project's issues down to the set that should appear in the PDF,
 * and projects each surviving issue onto the item shape the PDF document
 * expects.
 *
 * Every size flagged by the scanner is included. The scanner has already
 * done the shipping-matrix reasoning — destinations it reports are genuinely
 * unserviceable for someone, and destinations are already scoped to the
 * project's configured warehouses. No regional filter needed; the rep can
 * uncheck individual items in the modal if a specific item isn't relevant
 * to the customer they're emailing.
 */
function buildPDFItems({ project, productsById }) {
  const items = [];
  for (const issue of project.issues) {
    if (!ACTIVE_STATUSES.has(issue.status)) continue;
    const product = productsById[issue.product_id];
    if (!product) continue;

    const sortedSizes = [...(issue.oos_sizes || [])].sort(
      (a, b) => (SIZE_ORDER[a] ?? 99) - (SIZE_ORDER[b] ?? 99)
    );
    const qcPending = issue.qc_pending_since || {};
    const oosSizes = sortedSizes.map((size) => ({
      size,
      clearDate: issue.oos_size_clear_dates?.[size] ?? null,
      qcPending: Boolean(qcPending[size]),
    }));
    if (oosSizes.length === 0) continue;

    // For contacted + drifted rows, pass the diff through to the PDF so
    // the rep can open the customer call with "heads up, here's what's
    // moved since we talked" rather than being caught off-guard.
    const drift = computeIssueDrift(issue);

    items.push({
      issueId: issue.id,
      itemNumber: product.itemNumber || "",
      name: product.name || "",
      color: product.color || "",
      imageURL: product.imageURL || null,
      oosSizes,
      driftChanges: drift.drifted ? drift.changes : null,
      contactedAt: drift.drifted ? issue.contact_snapshot?.at ?? null : null,
    });
  }
  return items;
}

export default function PDFExportModal({
  show,
  onClose,
  project,
  productsById,
}) {
  const [projectDoc, setProjectDoc] = useState(null);
  const [loadingProject, setLoadingProject] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [generating, setGenerating] = useState(false);


  useEffect(() => {
    if (!show) {
      setProjectDoc(null);
      setLoadingProject(false);
      setSelectedIds(new Set());
      return;
    }
    if (!project?.firebaseId) return;
    let cancelled = false;
    setLoadingProject(true);
    const db = getFirestore();
    (async () => {
      try {
        const snap = await getDoc(doc(db, "projects", project.firebaseId));
        if (cancelled) return;
        setProjectDoc(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error("Failed to fetch project doc for PDF export", e);
        if (!cancelled) setProjectDoc(null);
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, project?.firebaseId]);

  const items = useMemo(() => {
    // Gate on projectDoc so we still show the loading state before the
    // Firestore fetch resolves (even though the filter doesn't need the doc).
    if (!projectDoc) return [];
    return buildPDFItems({ project, productsById });
  }, [project, productsById, projectDoc]);

  // Default every eligible item checked once the item list resolves.
  useEffect(() => {
    if (items.length === 0) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(items.map((i) => i.issueId)));
  }, [items]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((i) => i.issueId))
    );
  };

  const handleGenerate = async () => {
    if (generating) return;
    const picked = items.filter((i) => selectedIds.has(i.issueId));
    if (picked.length === 0) {
      toast.info("Select at least one item to include in the PDF");
      return;
    }
    setGenerating(true);
    try {
      // Dynamically import @react-pdf/renderer + the document component in
      // parallel with logo rasterization. Keeps the ~300KB PDF engine out of
      // the initial stock-issues bundle — it only arrives on the first click.
      // Pre-fetch logos to data URLs so react-pdf never has to handle
      // cross-origin fetches (which can fail silently, leaving a blank slot).
      // Logo preference: `project.logoURL` (Supabase search, same source the
      // card renders successfully via <img>) → Firestore `projectDoc.logoURL`
      // → nothing. If fetch-to-data-URL fails (CORS), fall back to the raw
      // URL so react-pdf can attempt its own fetch.
      const projectLogoURL =
        project?.logoURL || projectDoc?.logoURL || null;
      const [
        { pdf },
        { default: StockIssuesPDFDocument },
        logoSrc,
        footerLogoSrc,
      ] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./StockIssuesPDFDocument"),
        // Canvas-reencode to PNG so react-pdf always sees a known format.
        // Firebase uploads sometimes have mismatched extensions (a PNG saved
        // as `.jpg`), which tripped react-pdf's "SOI not found in JPEG"
        // check when we passed the raw URL through.
        projectLogoURL
          ? fetchAndDownscaleToPng(projectLogoURL, 280)
          : Promise.resolve(null),
        svgUrlToPngDataUrl(STORMTECH_FOOTER_LOGO, 700, 64),
      ]);

      // Downscale product shots before embedding — stock PDFs were landing at
      // 4-6MB when 20+ items each carried a full-res PNG. Product thumbs render
      // at ~72pt in the PDF, so 200px JPEG is plenty for print quality.
      const compressedItems = await Promise.all(
        picked.map(async (item) => ({
          ...item,
          imageURL: item.imageURL
            ? await fetchAndDownscaleToJpeg(item.imageURL, 200, 0.82)
            : null,
        }))
      );

      const blob = await pdf(
        <StockIssuesPDFDocument
          projectName={project.name}
          accountName={project.accountNumber}
          distributorName={project.distributor}
          repName={project.rep}
          logoSrc={logoSrc}
          footerLogoSrc={footerLogoSrc}
          items={compressedItems}
        />
      ).toBlob();

      const safeName = (project.name || "stock-issues")
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      const dateTag = new Date().toISOString().slice(0, 10);
      const filename = `stock-status-${safeName}-${dateTag}.pdf`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onClose();
    } catch (e) {
      console.error("PDF generation failed", e);
      toast.error(`PDF generation failed: ${e.message || e}`);
    } finally {
      setGenerating(false);
    }
  };

  let body;
  if (loadingProject) {
    body = <Empty>Loading project context…</Empty>;
  } else if (!projectDoc) {
    body = <Empty>Could not load project context. Cannot generate PDF.</Empty>;
  } else if (items.length === 0) {
    body = <Empty>No active stock issues to include on this project.</Empty>;
  } else {
    const allChecked = selectedIds.size === items.length;
    body = (
      <>
        <SelectAllRow>
          <SelectAllLabel>
            <Checkbox
              checked={allChecked}
              onChange={toggleAll}
              ariaLabel="Select all items"
            />
            <span>Select all ({items.length})</span>
          </SelectAllLabel>
        </SelectAllRow>
        <ItemList>
          {items.map((item) => {
            const checked = selectedIds.has(item.issueId);
            return (
              <ItemRow key={item.issueId}>
                <Checkbox
                  checked={checked}
                  onChange={() => toggle(item.issueId)}
                  ariaLabel={`Include ${item.itemNumber}`}
                />
                {item.imageURL ? (
                  <Thumb src={item.imageURL} alt="" />
                ) : (
                  <ThumbPlaceholder />
                )}
                <ItemText>
                  <ItemTitle>
                    {item.itemNumber} — {item.name}
                  </ItemTitle>
                  <ItemMeta>Color: {item.color}</ItemMeta>
                  <ItemMetaMuted>Out of stock</ItemMetaMuted>
                  <SizeColumns>
                    {splitIntoColumns(item.oosSizes).map((col, ci) => (
                      <SizeColumn key={ci}>
                        {col.map((s) => {
                          const humanized = humanizeDate(s.clearDate);
                          const severity = humanized ? "warning" : "critical";
                          return (
                            <SizeRow key={s.size}>
                              <SizeChip severity={severity}>{s.size}</SizeChip>
                              <SizeText>
                                {humanized
                                  ? `back ~ ${humanized}`
                                  : "no incoming"}
                              </SizeText>
                            </SizeRow>
                          );
                        })}
                      </SizeColumn>
                    ))}
                  </SizeColumns>
                </ItemText>
              </ItemRow>
            );
          })}
        </ItemList>
      </>
    );
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Export Stock Status PDF"
      maxWidth="720px"
      maxHeight="90vh"
    >
      <Body>
        <ContextLine>
          {project.name}
          {project.accountNumber ? ` · ${project.accountNumber}` : ""}
          {project.distributor ? ` · ${project.distributor}` : ""}
        </ContextLine>
        <Scrollable>{body}</Scrollable>
        <Footer>
          <Button type="button" cancel onClick={onClose} disabled={generating}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleGenerate}
            disabled={
              generating ||
              loadingProject ||
              items.length === 0 ||
              selectedIds.size === 0
            }
          >
            {generating ? "Generating…" : "Download PDF"}
          </Button>
        </Footer>
      </Body>
    </Modal>
  );
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(90vh - 120px);
`;

const ContextLine = styled.div`
  font-size: ${FONTSIZE.small};
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.medium};
  padding-bottom: 6px;
  border-bottom: 1px solid ${COLORS.secondary};
`;

const Scrollable = styled.div`
  overflow-y: auto;
  max-height: 60vh;
`;

const Empty = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: ${COLORS.stormtechGrey};
  font-size: ${FONTSIZE.small};
`;

const SelectAllRow = styled.div`
  padding: 6px 4px 10px;
  border-bottom: 1px dashed ${COLORS.secondary};
`;

const SelectAllLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: ${FONTWEIGHT.medium};
  color: ${COLORS.text};
`;

const CheckboxWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: pointer;
`;

const CheckboxBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid
    ${({ $checked }) => ($checked ? COLORS.text : COLORS.secondary)};
  background: ${({ $checked }) =>
    $checked ? COLORS.text : COLORS.primaryInverted};
  color: ${COLORS.primaryInverted};
  transition: background 0.12s ease, border-color 0.12s ease;

  ${HiddenInput}:hover + & {
    border-color: ${COLORS.stormtechGrey};
  }

  ${HiddenInput}:focus-visible + & {
    outline: 2px solid ${COLORS.stormtechGrey};
    outline-offset: 2px;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 6px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid ${COLORS.secondary};

  &:last-child {
    border-bottom: none;
  }

  ${CheckboxWrap} {
    margin-top: 3px;
  }
`;

const Thumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  flex-shrink: 0;
  background: ${COLORS.tertiary};
  border-radius: 4px;
`;

const ThumbPlaceholder = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  background: ${COLORS.tertiary};
  border-radius: 4px;
`;

const ItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.text};
`;

const ItemMetaMuted = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: uppercase;
  font-weight: ${FONTWEIGHT.medium};
  margin-top: 4px;
  margin-bottom: 2px;
`;

const SizeColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 2px;
`;

const SizeColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SizeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SEVERITY_CHIP = {
  critical: {
    color: COLORS.alertRed,
    bg: "rgba(204, 0, 0, 0.12)",
  },
  warning: {
    color: COLORS.alertOrange,
    bg: "rgba(247, 147, 30, 0.15)",
  },
};

const SizeChip = styled.span`
  /* Fixed width so every chip is identical and the detail text column
     lines up across rows. Sized for the widest label we expect ("EACH",
     "5XL") at xsmall weight-heavy. */
  display: inline-block;
  box-sizing: border-box;
  width: 46px;
  text-align: center;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  padding: 1px 0;
  border-radius: 3px;
  text-transform: uppercase;
  color: ${({ severity }) => SEVERITY_CHIP[severity].color};
  background: ${({ severity }) => SEVERITY_CHIP[severity].bg};
  border: 1px solid ${({ severity }) => SEVERITY_CHIP[severity].color};
`;

const SizeText = styled.span`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.text};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid ${COLORS.secondary};
`;
